import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Search,
    UserCircle,
    Car,
    AlertTriangle,
    AlertCircle,
    Building2,
    X,
} from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { Modal } from '../../components/ui/Modal.jsx';
import { Button } from '../../components/ui/Button.jsx';

// ---------------------------------------------------------------------------
// AllocateBookingModal
// ---------------------------------------------------------------------------
// Flow:
//   1. Dispatcher selects chauffeur (required) and optional vehicle.
//   2. Compliance + conflict checks run silently in the background.
//   3. Conflict warnings (chauffeur double-booked) surface above the Allocate
//      button as a soft amber banner. They don't block.
//   4. Click Allocate:
//      - All compliance passes → allocate immediately, close modal
//      - Compliance fails (HTTP 422) → show specific issues inline + owner
//        override path
//
// ApiError shape (lib/api.js): the wrapper attaches the full server response
// body to err.response. So for a 422 compliance_failed, err.response is:
//   { error: 'compliance_failed', chauffeur: {ok, issues}, vehicle: {ok, issues} }
// ---------------------------------------------------------------------------

export function AllocateBookingModal({ booking, onClose, onAllocated }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const { user } = useCurrentUser();
    const isOwner = user?.role === 'owner';

    const [selectedChauffeur, setSelectedChauffeur] = useState(
        booking.allocated_chauffeur || null
    );
    const [selectedVehicle, setSelectedVehicle] = useState(
        booking.allocated_vehicle || null
    );

    const [failedChecks, setFailedChecks] = useState(null);
    const [overrideMode, setOverrideMode] = useState(false);
    const [overrideReason, setOverrideReason] = useState('');
    const [submitError, setSubmitError] = useState(null);

    const isReallocation = !!booking.allocated_chauffeur_id;

    // ---- Background compliance + conflict checks ------------------------
    const { data: checks } = useQuery({
        queryKey: [
            'allocation-checks',
            booking.id,
            selectedChauffeur?.id,
            selectedVehicle?.id,
        ],
        queryFn: () => api.get(`/bookings/${booking.id}/allocation-checks`, {
            query: {
                chauffeur_id: selectedChauffeur.id,
                vehicle_id: selectedVehicle?.id || undefined,
            },
        }),
        enabled: !!selectedChauffeur,
        keepPreviousData: false,
    });

    const hasConflict = checks?.conflict?.has_conflict;
    const conflictCount = checks?.conflict?.overlapping_bookings?.length || 0;

    // ---- Allocate mutation ----------------------------------------------
    const allocateMutation = useMutation({
        mutationFn: () => api.post(`/bookings/${booking.id}/allocate`, {
            chauffeur_id: selectedChauffeur.id,
            vehicle_id: selectedVehicle?.id || null,
            override_reason: overrideMode ? overrideReason.trim() : null,
        }),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
            onAllocated?.(data);
            onClose();
        },
        onError: (err) => {
            // ApiError attaches the full server response body to err.response.
            // For 422 compliance_failed this contains chauffeur + vehicle issue lists.
            const body = err?.response;

            if (body?.error === 'compliance_failed') {
                setFailedChecks({
                    chauffeur: body.chauffeur,
                    vehicle: body.vehicle,
                });
                setSubmitError(null);
                return;
            }

            // Generic failure — show whatever message we can extract
            setSubmitError(err?.message || 'Allocation failed');
        },
    });

    function handleSubmit() {
        setSubmitError(null);
        allocateMutation.mutate();
    }

   function resetFailureState() {
        setFailedChecks(null);
        setOverrideMode(false);
        setOverrideReason('');
        setSubmitError(null);
    }

    // Default the vehicle to the chauffeur's paired (soft) vehicle. Blank if
    // none. Fires only on active selection (not mount), so an existing
    // allocation isn't clobbered. Dispatcher can still override after.
    async function applyPairedVehicle(chauffeurId) {
        try {
            const pairing = await api.get(`/drivers/${chauffeurId}/pairing`);
            if (pairing && pairing.vehicle_id) {
                setSelectedVehicle({
                    id: pairing.vehicle_id,
                    nickname: pairing.vehicle_nickname,
                    make: pairing.make,
                    model: pairing.model,
                    registration: pairing.registration,
                    vehicle_class: pairing.vehicle_class,
                    fleet_partner_id: pairing.fleet_partner_id,
                    fleet_partner_company_name: pairing.fleet_partner_company_name,
                });
            }
        } catch {
            // No pairing (or fetch failed) — leave the vehicle blank.
        }
    }

    const canSubmit =
        selectedChauffeur &&
        !allocateMutation.isPending &&
        (!overrideMode || (overrideReason.trim().length > 0 && isOwner));

    return (
        <Modal
            open
            onClose={onClose}
            title={isReallocation ? 'Re-allocate booking' : 'Allocate booking'}
            description={
                isReallocation
                    ? `Currently allocated to ${booking.allocated_chauffeur?.first_name || 'unknown'}. Selecting a new chauffeur resets status to allocated.`
                    : `Pickup: ${formatPickup(booking.ride_datetime_local)}`
            }
            size="lg"
        >
            <div className="space-y-5">
                {/* Chauffeur picker */}
                <div>
                    <label className="block text-xs font-medium text-ink-subtle uppercase tracking-wide mb-1.5">
                        Chauffeur <span className="text-red-500">*</span>
                    </label>
                    <ChauffeurPicker
                        value={selectedChauffeur}
                        onChange={(c) => {
                            setSelectedChauffeur(c);
                            setSelectedVehicle(null);
                            resetFailureState();
                            if (c) applyPairedVehicle(c.id);
                        }}
                    />
                </div>

                {/* Vehicle picker */}
                <div>
                    <label className="block text-xs font-medium text-ink-subtle uppercase tracking-wide mb-1.5">
                        Vehicle
                    </label>
                    <VehiclePicker
                        value={selectedVehicle}
                        onChange={(v) => {
                            setSelectedVehicle(v);
                            resetFailureState();
                        }}
                    />
                </div>

                {/* Conflict warning — soft banner, doesn't block */}
                {hasConflict && !failedChecks && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-sm">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-amber-900">
                            <div className="font-medium">Scheduling conflict</div>
                            <div className="text-xs text-amber-800 mt-0.5">
                                {selectedChauffeur?.first_name} has {conflictCount} other booking{conflictCount === 1 ? '' : 's'} in the pickup window. Allocate anyway if you've checked.
                            </div>
                        </div>
                    </div>
                )}

                {/* Compliance failure details */}
                {failedChecks && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2">
                        <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-red-900 font-medium">
                                Cannot allocate — compliance check failed
                            </div>
                        </div>
                        <ul className="ml-6 space-y-1 text-sm text-red-800 list-disc list-inside">
                            {!failedChecks.chauffeur?.ok && (failedChecks.chauffeur?.issues || []).map((issue, i) => (
                                <li key={`c-${i}`}>
                                    <span className="font-medium">Chauffeur:</span> {formatIssue(issue)}
                                </li>
                            ))}
                            {failedChecks.vehicle && !failedChecks.vehicle.ok && (failedChecks.vehicle.issues || []).map((issue, i) => (
                                <li key={`v-${i}`}>
                                    <span className="font-medium">Vehicle:</span> {formatIssue(issue)}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Owner override */}
                {failedChecks && isOwner && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={overrideMode}
                                onChange={(e) => setOverrideMode(e.target.checked)}
                                className="mt-0.5"
                            />
                            <div className="text-sm">
                                <div className="font-medium text-amber-900">
                                    Override and allocate anyway
                                </div>
                                <div className="text-xs text-amber-800 mt-0.5">
                                    Owner-only. Reason will be audit-logged on this booking.
                                </div>
                            </div>
                        </label>
                        {overrideMode && (
                            <textarea
                                value={overrideReason}
                                onChange={(e) => setOverrideReason(e.target.value)}
                                placeholder="Why are you overriding compliance? (required)"
                                rows={2}
                                className="w-full text-sm px-2 py-1.5 border border-amber-300 rounded focus:outline-none focus:border-amber-500"
                                autoFocus
                            />
                        )}
                    </div>
                )}

                {failedChecks && !isOwner && (
                    <div className="text-xs text-ink-subtle px-1">
                        Only owner accounts can override compliance. Update the documents or contact an owner.
                    </div>
                )}

                {submitError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                        {submitError}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-paper-border-soft">
                    <Button variant="ghost" onClick={onClose} disabled={allocateMutation.isPending}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                    >
                        {allocateMutation.isPending
                            ? 'Allocating…'
                            : (overrideMode
                                ? 'Override and allocate'
                                : (isReallocation ? 'Re-allocate' : 'Allocate'))}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}


// ===========================================================================
// ChauffeurPicker
// ===========================================================================
function ChauffeurPicker({ value, onChange }) {
    const api = useApi();
    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [open, setOpen] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query), 200);
        return () => clearTimeout(t);
    }, [query]);

    const { data, isFetching } = useQuery({
        queryKey: ['drivers', 'search', debounced],
        queryFn: () => api.get('/drivers', {
            query: {
                search: debounced || undefined,
                status: 'active',
                limit: 10,
            },
        }),
        enabled: open && debounced.length > 0,
    });

    const results = data?.drivers || [];

    if (value && !open) {
        return (
            <button
                type="button"
                onClick={() => {
                    setQuery('');
                    setOpen(true);
                    setTimeout(() => inputRef.current?.focus(), 0);
                }}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-paper-border rounded-lg hover:bg-canvas text-left"
            >
                <div className="flex items-center gap-2 min-w-0">
                    <UserCircle className="h-5 w-5 text-ink-subtle flex-shrink-0" />
                    <div className="min-w-0">
                        <div className="text-sm font-medium text-ink truncate">
                            {value.first_name} {value.last_name}
                            {value.call_sign && (
                                <span className="ml-2 text-xs text-ink-subtle">({value.call_sign})</span>
                            )}
                        </div>
                        <div className="text-xs text-ink-subtle truncate">
                            {value.phone}
                        </div>
                    </div>
                </div>
                {value.fleet_partner_id && (
                    <FleetPartnerBadge name={value.fleet_partner_company_name} />
                )}
            </button>
        );
    }

    return (
        <div className="relative">
            <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle" />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    placeholder="Start typing chauffeur name…"
                    className="w-full text-sm pl-8 pr-3 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                    autoFocus={!value}
                />
                {value && (
                    <button
                        type="button"
                        onClick={() => {
                            onChange(null);
                            setQuery('');
                            setOpen(true);
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-canvas rounded"
                        aria-label="Clear chauffeur"
                    >
                        <X className="h-3.5 w-3.5 text-ink-subtle" />
                    </button>
                )}
            </div>

            {open && debounced.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-paper-border rounded-lg shadow-lg max-h-72 overflow-auto">
                    {isFetching && (
                        <div className="px-3 py-2 text-sm text-ink-subtle">Searching…</div>
                    )}
                    {!isFetching && results.length === 0 && (
                        <div className="px-3 py-2 text-sm text-ink-subtle">No matching chauffeurs.</div>
                    )}
                    {results.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => {
                                onChange(c);
                                setOpen(false);
                            }}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-canvas text-left border-b border-paper-border-soft last:border-0"
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <UserCircle className="h-5 w-5 text-ink-subtle flex-shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-ink truncate">
                                        {c.first_name} {c.last_name}
                                        {c.call_sign && (
                                            <span className="ml-2 text-xs text-ink-subtle">({c.call_sign})</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-ink-subtle truncate">{c.phone}</div>
                                </div>
                            </div>
                            {c.fleet_partner_id && (
                                <FleetPartnerBadge name={c.fleet_partner_company_name} />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}


// ===========================================================================
// VehiclePicker
// ===========================================================================
function VehiclePicker({ value, onChange }) {
    const api = useApi();
    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [open, setOpen] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query), 200);
        return () => clearTimeout(t);
    }, [query]);

    const { data, isFetching } = useQuery({
        queryKey: ['vehicles', 'search', debounced],
        queryFn: () => api.get('/vehicles', {
            query: {
                search: debounced || undefined,
                status: 'active',
                limit: 10,
            },
        }),
        enabled: open && debounced.length > 0,
    });

    const results = data?.vehicles || [];

    if (value && !open) {
        return (
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => {
                        setQuery('');
                        setOpen(true);
                        setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                    className="flex-1 flex items-center justify-between gap-2 px-3 py-2 border border-paper-border rounded-lg hover:bg-canvas text-left"
                >
                    <div className="flex items-center gap-2 min-w-0">
                        <Car className="h-5 w-5 text-ink-subtle flex-shrink-0" />
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-ink truncate">
                                {value.nickname || `${value.make} ${value.model}`}
                            </div>
                            <div className="text-xs text-ink-subtle truncate">
                                {value.registration}{value.vehicle_class ? ` · ${value.vehicle_class}` : ''}
                            </div>
                        </div>
                    </div>
                    {value.fleet_partner_id && (
                        <FleetPartnerBadge name={value.fleet_partner_company_name} />
                    )}
                </button>
                <button
                    type="button"
                    onClick={() => onChange(null)}
                    className="p-2 text-ink-subtle hover:bg-canvas rounded"
                    aria-label="Clear vehicle"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
        );
    }

    return (
        <div className="relative">
            <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle" />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    placeholder="Start typing vehicle reg or nickname…"
                    className="w-full text-sm pl-8 pr-3 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                />
            </div>

            {open && debounced.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-paper-border rounded-lg shadow-lg max-h-72 overflow-auto">
                    {isFetching && (
                        <div className="px-3 py-2 text-sm text-ink-subtle">Searching…</div>
                    )}
                    {!isFetching && results.length === 0 && (
                        <div className="px-3 py-2 text-sm text-ink-subtle">No matching vehicles.</div>
                    )}
                    {results.map((v) => (
                        <button
                            key={v.id}
                            type="button"
                            onClick={() => {
                                onChange(v);
                                setOpen(false);
                            }}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-canvas text-left border-b border-paper-border-soft last:border-0"
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <Car className="h-5 w-5 text-ink-subtle flex-shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-ink truncate">
                                        {v.nickname || `${v.make} ${v.model}`}
                                    </div>
                                    <div className="text-xs text-ink-subtle truncate">
                                        {v.registration} · {v.vehicle_class}
                                    </div>
                                </div>
                            </div>
                            {v.fleet_partner_id && (
                                <FleetPartnerBadge name={v.fleet_partner_company_name} />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}


// ===========================================================================
// Helpers
// ===========================================================================

function FleetPartnerBadge({ name }) {
    return (
        <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[10px] font-medium uppercase tracking-wide flex-shrink-0"
            title={name ? `Fleet partner: ${name}` : 'Fleet partner'}
        >
            <Building2 className="h-3 w-3" />
            Fleet
        </span>
    );
}

function formatIssue(issue) {
    switch (issue.type) {
        case 'missing_document':
            return `${issue.detail} not on file`;
        case 'expired_document':
        case 'expiring_soon':
            return issue.detail;
        case 'driver_not_active':
            return issue.detail || 'Chauffeur not active';
        case 'driver_engagement_ended':
            return issue.detail || 'Engagement end date passed';
        case 'driver_not_found':
            return 'Chauffeur not found';
        case 'vehicle_not_active':
            return issue.detail || 'Vehicle not active';
        case 'vehicle_not_found':
            return 'Vehicle not found';
        default:
            return issue.detail || issue.type;
    }
}

function formatPickup(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
